import { useMemo } from 'react';
import { useQuery } from 'urql';
import { groupBy } from 'lodash';
import {
  ModelsNameMap,
  ShapeFoldersMap,
  ShapesMap,
  SheetCalibrationsMap,
  SparkelPropertiesMap,
  useViewer,
} from '../components/common/ForgeViewer';
import {
  PropertyContext,
  SheetShapeWithSheetInfoMap,
  SparkelPropertiesIndex,
} from '../domain/property-operations';
import {
  AutoShapeDeepFragment,
  GetActiveModelsForProjectDocument,
  GetActiveSheetsForProjectDocument,
  GetAutoShapesForProjectDocument,
  GetShapeFoldersForProjectDeepDocument,
  GetShapesForProjectDeepDocument,
  GetSheetShapesForProjectDeepDocument,
  GetSparkelPropertiesForProjectDocument,
} from '../gql/graphql';
import { getShapeUrn } from '../services/viewer/ShapesManager';
import { useMeshAlgorithmResults } from './mesh-algorithm-results';

// eslint-disable-next-line complexity
export function usePropertyContext(projectId: string) {
  const { isAllModelsLoaded, loadedModels } = useViewer();
  const {
    meshAlgorithmResults,
    loading: loadingMeshAlgorithmResults,
    isProTier,
  } = useMeshAlgorithmResults(projectId);

  const [
    {
      data: shapesData,
      fetching: fetchingShapes,
      error: shapesError,
      stale: shapesStale,
    },
  ] = useQuery({
    query: GetShapesForProjectDeepDocument,
    variables: {
      projectId,
    },
  });

  const [
    {
      data: sparkelPropertiesData,
      fetching: fetchingSparkelProperties,
      error: sparkelPropertiesError,
      stale: sparkelPropertiesStale,
    },
  ] = useQuery({
    query: GetSparkelPropertiesForProjectDocument,
    variables: {
      projectId,
    },
  });

  const [
    {
      data: sheetShapesData,
      fetching: fetchingSheetShapes,
      error: sheetShapesError,
      stale: sheetShapesStale,
    },
  ] = useQuery({
    query: GetSheetShapesForProjectDeepDocument,
    variables: {
      projectId,
    },
  });

  const [
    {
      data: activeModels,
      fetching: fetchingaAtiveModels,
      error: activeModelsError,
      stale: activeModelsStale,
    },
  ] = useQuery({
    query: GetActiveModelsForProjectDocument,
    variables: {
      projectId,
    },
  });

  const [
    {
      data: shapeFoldersData,
      fetching: fetchingShapeFolders,
      error: shapeFoldersError,
      stale: shapeFoldersStale,
    },
  ] = useQuery({
    query: GetShapeFoldersForProjectDeepDocument,
    variables: {
      projectId,
    },
  });
  const [
    {
      data: autoShapesData,
      fetching: fetchingAutoShapes,
      error: autoShapesError,
      stale: autoShapesStale,
    },
  ] = useQuery({
    query: GetAutoShapesForProjectDocument,
    variables: {
      projectId,
    },
  });
  const [
    {
      data: sheetCalibrationsData,
      fetching: fetchingSheetCalibrations,
      error: sheetCalibrationsError,
      stale: sheetCalibrationsStale,
    },
  ] = useQuery({
    query: GetActiveSheetsForProjectDocument,
    variables: {
      projectId,
    },
  });

  // eslint-disable-next-line complexity
  const propertyContext = useMemo<PropertyContext>(() => {
    let loadedShapes: ShapesMap = {};
    let loadedSheetShapes: SheetShapeWithSheetInfoMap = {};
    let loadedShapeFolders: ShapeFoldersMap = {};
    let loadedAutoShapes: AutoShapeDeepFragment[] = [];
    let loadedSheetCalibrations: SheetCalibrationsMap = {};
    let loadedSparkelProperties: SparkelPropertiesMap = {};
    let loadedActiveModelsNameMap: ModelsNameMap = {};
    let loadedSheets: Array<{
      id: string;
      name: string;
      filename: string;
      version?: number;
    }> = [];
    let loadedSparkelPropertiesIndex: SparkelPropertiesIndex | undefined;

    if (
      shapesData?.project &&
      shapeFoldersData?.project &&
      autoShapesData?.project &&
      sheetShapesData?.project &&
      sheetCalibrationsData?.project?.activeSheets &&
      activeModels?.project?.activeModels
    ) {
      loadedShapes = groupBy(shapesData.project.shapes, (shape) => shape.urn);

      loadedShapeFolders = Object.fromEntries([
        [getShapeUrn(projectId), shapeFoldersData.project.shapeFolders],
      ]);

      loadedAutoShapes = autoShapesData.project.autoShapes;

      loadedSheetCalibrations = Object.fromEntries(
        sheetCalibrationsData.project.activeSheets.map((sheet) => [
          sheet.id,
          sheet.sheetPageCalibrations,
        ])
      );

      loadedActiveModelsNameMap = Object.fromEntries(
        activeModels.project.activeModels?.map((activeModel) => [
          activeModel.id + '.ifc',
          activeModel.filename,
        ])
      );

      const sheets = sheetCalibrationsData.project.activeSheets;
      loadedSheets = sheets.map((s) => ({
        id: s.id,
        name: s.name,
        filename: s.filename,
        version: s.version,
      }));
      const sheetShapesWithSheet = sheetShapesData.project.sheetShapes.map(
        (sheetShape) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const sheet = sheets!.find(
            (sheet) => sheet.id === sheetShape.sheetId
          )!;
          return {
            ...sheetShape,
            sheet: {
              id: sheet.id,
              name: sheet.name,
              filename: sheet.filename,
            },
          };
        }
      );
      loadedSheetShapes = groupBy(
        sheetShapesWithSheet,
        (sheetShape) => sheetShape.urn
      );
    }

    if (sparkelPropertiesData?.project) {
      const project = sparkelPropertiesData.project;
      loadedSparkelProperties =
        project.sparkelProperties.reduce<SparkelPropertiesMap>(
          (propertiesMap, property) => {
            const urn = property.modelUrn;
            if (urn in propertiesMap) {
              propertiesMap[urn].push(property);
            } else {
              propertiesMap[urn] = [property];
            }
            return propertiesMap;
          },
          {}
        );

      // Build fast index: urn -> propertySet -> (dbId -> value)
      const index: SparkelPropertiesIndex = {};
      for (const prop of project.sparkelProperties) {
        const urn = prop.modelUrn;
        if (!index[urn]) index[urn] = {};
        const bySet = index[urn];
        if (!bySet[prop.propertySet]) bySet[prop.propertySet] = new Map();
        bySet[prop.propertySet].set(prop.dbId, prop.propertyValue);
      }
      loadedSparkelPropertiesIndex = index;
    }

    return {
      loadedModels,
      loadedShapes,
      loadedSheetShapes,
      loadedShapeFolders,
      loadedAutoShapes,
      loadedSheetCalibrations,
      loadedSparkelProperties,
      loadedSparkelPropertiesIndex,
      loadedActiveModelsNameMap,
      loadedSheets,
      meshAlgorithmResults,
    };
  }, [
    shapesData?.project,
    shapeFoldersData?.project,
    autoShapesData?.project,
    sheetShapesData?.project,
    sheetCalibrationsData?.project?.activeSheets,
    activeModels?.project?.activeModels,
    sparkelPropertiesData?.project,
    loadedModels,
    projectId,
    meshAlgorithmResults,
  ]);

  return {
    propertyContext,
    isAllPropertiesLoaded:
      !fetchingShapes &&
      !fetchingSheetShapes &&
      !fetchingShapeFolders &&
      !fetchingAutoShapes &&
      !fetchingSheetCalibrations &&
      !fetchingaAtiveModels &&
      !activeModelsStale &&
      !shapesStale &&
      !sheetShapesStale &&
      !shapeFoldersStale &&
      !autoShapesStale &&
      !sparkelPropertiesStale &&
      !sheetCalibrationsStale &&
      !fetchingSparkelProperties &&
      isAllModelsLoaded &&
      // Only wait for mesh algorithm results loading on pro tier
      (!isProTier || !loadingMeshAlgorithmResults),
    error:
      shapesError ||
      activeModelsError ||
      sheetShapesError ||
      shapeFoldersError ||
      autoShapesError ||
      sheetCalibrationsError ||
      sparkelPropertiesError,
  };
}
